<template>
  <div :class="{[$style.readonly]: readonly}">
    <v-text-field
      v-if="type !== 'number' && type !== 'password'"
      :class="classes"
      outlined
      v-bind="$attrs"
      v-on="$listeners"
      flat
      :type="type"
    >
      <slot
        v-for="(_, name) in $slots"
        :slot="name"
        :name="name"
      />

      <template
        v-for="(_, name) in $scopedSlots"
        :slot="name"
        slot-scope="slotData"
      >
        <slot
          :name="name"
          v-bind="slotData"
        />
      </template>
    </v-text-field>
    <v-text-field
      v-else-if="type === 'password'"
      :class="classes"
      outlined
      v-bind="$attrs"
      v-on="$listeners"
      flat
      :append-icon="show_pass ? 'mdi-eye-off' : 'mdi-eye'"
      @click:append="show_pass = !show_pass"
      :type="show_pass ? 'text' : 'password'"
    >
      <slot
        v-for="(_, name) in $slots"
        :slot="name"
        :name="name"
      />

      <template
        v-for="(_, name) in $scopedSlots"
        :slot="name"
        slot-scope="slotData"
      >
        <slot
          :name="name"
          v-bind="slotData"
        />
      </template>
    </v-text-field>
    <template v-else>
      <v-text-field
        v-if="hide_btns"
        v-bind="$attrs"
        flat
        outlined
        :class="classes"
        @paste="paste"
        @input="input"
        @blur="on_blur"
        :key="upd_key"
        type="number"
      />
      <v-text-field
        v-else
        v-bind="$attrs"
        flat
        outlined
        :class="classes"
        @paste="paste"
        @input="input"
        @blur="on_blur"
        :key="upd_key"
        type="number"
      >
        <v-icon
          @click="increment"
          slot="append-outer"
          color="accent darken-4"
          :disabled="isCounterLoading"
        >
          mdi-plus
        </v-icon>
        <v-icon
          @click="decrement"
          slot="prepend"
          color="accent darken-4"
          :disabled="isCounterLoading"
        >
          mdi-minus
        </v-icon>
      </v-text-field>
    </template>
  </div>
</template>

<script lang="ts">
import {
  Component,
  Emit,
  Prop,
  Vue,
} from 'vue-property-decorator';
import { cart_store } from '@/store';

@Component
export default class TextField extends Vue {
  @Prop({ type: Boolean, default: false })
  blue_icon!: boolean;

  @Prop({ type: Boolean, default: false })
  readonly!: boolean;

  @Prop({ type: Boolean, default: false })
  require!: boolean;

  @Prop({ type: String, default: 'text' })
  type!: string;

  /**
   * можно ли указывать отрицательные значения
   */
  @Prop({ type: Boolean, default: false })
  allow_negative!: boolean;

  @Prop({ type: Boolean, default: false })
  hide_btns!: boolean;

  @Prop({
    type: [
      Number,
      String,
    ],
    default: -1,
  })
  max!: string;

  @Prop({
    type: [
      Number,
      String,
    ],
    default: 0,
  })
  min!: string;

  @Prop({ type: Boolean, default: false })
  centered!: boolean;

  @Prop({ type: Boolean, default: false })
  small_error!: boolean;

  @Emit()
  input(val: string | number) {
    if (Number.isNaN(Number(this.$attrs.value)) || val === '') {
      return 0;
    }

    if (Number(this.max) > 0 && Number(val) > Number(this.max)) {
      return this.max;
    }

    if (Number(val) < Number(this.min)) {
      return this.min;
    }

    return val;
  }

  upd_key = 0;

  show_pass = false;

  get isCounterLoading() {
    return cart_store.state.isCounterLoading;
  }

  get classes() {
    return [
      'text-field',
      { 'text-field-centered': this.centered },
      { 'text-field-small-error': this.small_error },
      { blue_icon: this.blue_icon },
      { require: this.require },
    ];
  }

  on_blur() {
    const val = Number(this.$attrs.value);
    const max = Number(this.max);
    const min = Number(this.min);

    if (!Number.isNaN(val)) {
      if (max > 0 && val > max) {
        this.$attrs.value = this.max;
      } else if (val < min) {
        if (!this.allow_negative) {
          this.$attrs.value = this.min;
        }
      }
    } else {
      this.$attrs.value = '0';
    }

    this.upd_key++;
  }

  paste(event: ClipboardEvent) {
    const pasteVal = event.clipboardData?.getData('Text');

    if (Number.isNaN(Number(pasteVal))) {
      event.preventDefault();
    }
  }

  increment() {
    if (this.isCounterLoading) {
      return;
    }
    const val = Number(this.$attrs.value);
    const max = Number(this.max);

    if (Number.isNaN(val)) {
      this.$attrs.value = '0';
    } else if (max > 0 && (val + 1) > max) {
      this.$attrs.value = this.max;
    } else {
      this.$attrs.value = String(val + 1);
    }

    this.input(this.$attrs.value);
  }

  decrement() {
    if (this.isCounterLoading) {
      return;
    }
    const current_num = Number(this.$attrs.value);

    if (Number.isNaN(current_num)) {
      this.$attrs.value = '0';
    } else if (this.allow_negative) {
      this.$attrs.value = String(current_num - 1);
    } else {
      this.$attrs.value = current_num <= 0 ? '0' : String(current_num - 1);
    }

    this.input(this.$attrs.value);
  }
}
</script>

<style lang="scss" src="../inputs.scss" />

<style module lang="scss">
@import '../inputs.scss';

.readonly {
  * {
    padding: 0 !important;
    color: inherit !important;
    border: none !important;
  }

  :global(.text-field.v-input--is-disabled .v-input__control > .v-input__slot) {
      background-color: #fff !important;
  }
}
</style>
